import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarioAnual, DiaCalendario, HitoCalendario } from '../../../core/models';
import { CalendarioService } from '../../../core/services/calendario.service';

type FiltroEstadoCalendario = HitoCalendario['estado'] | 'Todos';

@Component({
  selector: 'app-calendario-anual',
  imports: [CommonModule, FormsModule],
  templateUrl: './calendario-anual.html',
  styleUrl: './calendario-anual.css',
})
export class CalendarioAnualComponent implements OnInit {
  // Configuracion base de la vista.
  readonly anioActualSistema = new Date().getFullYear();
  readonly diasSemana = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
  readonly estadosDisponibles: FiltroEstadoCalendario[] = ['Todos', 'abierto', 'cerrado', 'en_evaluacion'];
  readonly estadoDefault: FiltroEstadoCalendario = 'Todos';
  readonly subdireccionDefault = 'Todas';

  vistaActual: 'mes' | 'anio' = 'mes';
  anioSeleccionado = this.anioActualSistema;
  anioInput = this.anioActualSistema;
  mesSeleccionado = new Date().getMonth();

  nombresMeses: string[] = [];
  subdireccionesDisponibles: string[] = [];
  estadoSeleccionado: FiltroEstadoCalendario = this.estadoDefault;
  subdireccionSeleccionada = this.subdireccionDefault;

  calendario: CalendarioAnual | null = null;
  semanasMes: DiaCalendario[][] = [];
  private hitosMesSinFiltrar: HitoCalendario[] = [];

  constructor(private readonly calendarioService: CalendarioService) {}

  ngOnInit(): void {
    // Carga inicial de meses y calendario actual.
    this.nombresMeses = this.calendarioService.getNombresMeses();
    this.cargarCalendario(this.anioSeleccionado);
  }

  // Cambia entre vista mensual y anual.
  cambiarVista(vista: 'mes' | 'anio'): void {
    this.vistaActual = vista;
  }

  // Cambia el mes activo y reconstruye la grilla.
  seleccionarMes(index: number): void {
    this.mesSeleccionado = index;
    this.cargarVistaMes(this.anioSeleccionado, this.mesSeleccionado);
  }

  // Filtra hitos del mes en memoria para no recalcular por backend.
  aplicarFiltros(): void {
    const hitosFiltrados = this.hitosMesSinFiltrar.filter((hito) => {
      const coincideEstado = this.estadoSeleccionado === this.estadoDefault || hito.estado === this.estadoSeleccionado;
      const coincideSubdireccion =
        this.subdireccionSeleccionada === this.subdireccionDefault || hito.subdireccion === this.subdireccionSeleccionada;

      return coincideEstado && coincideSubdireccion;
    });

    this.semanasMes = this.construirGrillaMensual(this.anioSeleccionado, this.mesSeleccionado, hitosFiltrados);
  }

  // Restablece filtros al estado neutro.
  limpiarFiltros(): void {
    this.estadoSeleccionado = this.estadoDefault;
    this.subdireccionSeleccionada = this.subdireccionDefault;
    this.aplicarFiltros();
  }

  etiquetaEstado(estado: FiltroEstadoCalendario): string {
    if (estado === 'abierto') return 'Abierto';
    if (estado === 'cerrado') return 'Cerrado';
    if (estado === 'en_evaluacion') return 'En evaluacion';
    return 'Todos';
  }

  irAnioAnterior(): void {
    this.cargarCalendario(this.anioSeleccionado - 1);
  }

  irAnioSiguiente(): void {
    this.cargarCalendario(this.anioSeleccionado + 1);
  }

  // Valida rango permitido para el input de anio.
  cargarAnioInput(): void {
    const anioNormalizado = Number(this.anioInput);
    if (!Number.isInteger(anioNormalizado) || anioNormalizado < 1900 || anioNormalizado > 2500) {
      this.anioInput = this.anioSeleccionado;
      return;
    }

    this.cargarCalendario(anioNormalizado);
  }

  private cargarCalendario(anio: number): void {
    this.anioSeleccionado = anio;
    this.anioInput = anio;

    this.calendarioService.getCalendarioPorAnio(anio).subscribe((data) => {
      this.calendario = data;
    });

    this.cargarVistaMes(anio, this.mesSeleccionado);
  }

  private cargarVistaMes(anio: number, mesIndex: number): void {
    this.calendarioService.getHitosPorMes(anio, mesIndex).subscribe((hitosMes) => {
      this.hitosMesSinFiltrar = hitosMes;
      this.actualizarOpcionesSubdireccion(hitosMes);
      this.aplicarFiltros();
    });
  }

  // Recalcula subdirecciones disponibles en el mes activo.
  private actualizarOpcionesSubdireccion(hitosMes: HitoCalendario[]): void {
    this.subdireccionesDisponibles = [...new Set(hitosMes.map((hito) => hito.subdireccion))].sort();

    if (
      this.subdireccionSeleccionada !== this.subdireccionDefault &&
      !this.subdireccionesDisponibles.includes(this.subdireccionSeleccionada)
    ) {
      this.subdireccionSeleccionada = this.subdireccionDefault;
    }
  }

  // Genera una grilla fija 6x7 para mantener layout consistente.
  private construirGrillaMensual(anio: number, mesIndex: number, hitosMes: HitoCalendario[]): DiaCalendario[][] {
    const primerDiaMes = new Date(anio, mesIndex, 1);
    const ultimoDiaMes = new Date(anio, mesIndex + 1, 0);

    const offsetLunes = (primerDiaMes.getDay() + 6) % 7;
    const totalCeldas = 42;
    const ultimoDiaMesAnterior = new Date(anio, mesIndex, 0).getDate();
    const mapaHitos = this.agruparHitosPorFecha(hitosMes);

    const dias: DiaCalendario[] = [];
    for (let i = 0; i < totalCeldas; i += 1) {
      const diaRelativo = i - offsetLunes + 1;

      if (diaRelativo < 1) {
        const dia = ultimoDiaMesAnterior + diaRelativo;
        dias.push(this.crearDia(new Date(anio, mesIndex - 1, dia), dia, false, mapaHitos));
        continue;
      }

      if (diaRelativo > ultimoDiaMes.getDate()) {
        const dia = diaRelativo - ultimoDiaMes.getDate();
        dias.push(this.crearDia(new Date(anio, mesIndex + 1, dia), dia, false, mapaHitos));
        continue;
      }

      dias.push(this.crearDia(new Date(anio, mesIndex, diaRelativo), diaRelativo, true, mapaHitos));
    }

    return Array.from({ length: 6 }, (_, i) => dias.slice(i * 7, i * 7 + 7));
  }

  private crearDia(
    fecha: Date,
    dia: number,
    esMesActual: boolean,
    mapaHitos: Record<string, HitoCalendario[]>
  ): DiaCalendario {
    const iso = this.aIsoLocal(fecha);
    return { dia, esMesActual, hitos: mapaHitos[iso] ?? [] };
  }

  private agruparHitosPorFecha(hitos: HitoCalendario[]): Record<string, HitoCalendario[]> {
    return hitos.reduce<Record<string, HitoCalendario[]>>((acc, hito) => {
      if (!acc[hito.fecha]) acc[hito.fecha] = [];
      acc[hito.fecha].push(hito);
      return acc;
    }, {});
  }

  private aIsoLocal(fecha: Date): string {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
